import { UniButton } from '@/design-system/components/buttons';
import { Divider } from '@/design-system/components/Divider';
import { BottomSheetHeader } from '@/design-system/components/headers/BottomSheetHeader';
import { Icon } from '@/design-system/components/icons/Icon';
import { NativeModalView } from '@/design-system/components/modal/nativeModal/NativeModalView';
import { useNativeModalView } from '@/design-system/components/modal/nativeModal/useNativeModalView';
import { TextBody, TextCaption } from '@/design-system/components/typography';
import { UtxAppEmbed } from '@/design-system/components/utxAppEmbedCallback/utxAppEmbedCallback';
import { useBottomsheet } from '@/devtools/screens_deprecated/useBottomsheet.hook';
import BottomSheet, { BottomSheetBackdrop, useBottomSheetDynamicSnapPoints } from '@gorhom/bottom-sheet';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { LayoutContext } from 'contexts/LayoutContext';
import { ScrollView, SxProp, View } from 'dripsy';
import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'store/store';
import { RootStackRoutes } from 'types';
import { BillPaymentStackRoutes } from '@/features/bill-payment/payment.navigator';
import { FundSourceSelectFilters, RemittancePurposeSelectFilters } from './options';
import { UnionPayStackRoutes } from '@/features/bill-payment/screens/union-pay/type';
import { formatAmountByDecimal } from '@/features/account-management/utils';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { CurrenciesIcon } from '@/features/earn/components';
import { UnionType } from '@/features/bill-payment/enum';
import { usePayout } from '@/features/account-management/screens/details/hooks/usePayouts.hook';
import { CreatePayoutBodyDestination, CreatePayoutBodyPurpose, CreatePayoutBodySourceOfFunds } from '@utx/utx-access-client';
import { isEmpty, isString } from 'lodash';
import { errorParser } from '../otp/type';
import { AlertType, UniAlert } from '@/design-system/components/alerts';

export interface ConfirmationNativeScreenProps {
  onBack?: (flag?: boolean) => void;
  wallet?: any;
  calcFee?: any;
  beneficiaryInformation?: any;
  unionType?: UnionType.PAYMENT | UnionType.ACCOUNT;
  refresh?: () => void;
  navigationNavigate?: string;
  navigationScreen?: string;
}

export const ConfirmationNativeScreen: React.FC<ConfirmationNativeScreenProps> = (props) => {
  const {
    onBack,
    wallet,
    calcFee,
    beneficiaryInformation,
    unionType,
    refresh,
    navigationNavigate = RootStackRoutes.BillPayment,
    navigationScreen = BillPaymentStackRoutes.UnionPay
  } = props;
  const { t } = useTranslation('bill-payment-union-pay');
  const navigation = useNavigation();
  const navigationAny = useNavigation<any>();
  const { bottomsheetRef, onOpen, onClose } = useBottomsheet();
  const { bottomsheetRef: bottomSheetOTPRef, onOpen: onOpenOTP, onClose: onCloseOTP } = useBottomsheet();
  const isFocused = useIsFocused();
  const store = useStore();

  const layout = useContext(LayoutContext);
  const renderBackdrop = useCallback(
    (props) => <BottomSheetBackdrop {...props} disappearsOnIndex={0} appearsOnIndex={1} pressBehavior={'none'} enableTouchThrough={true} />,
    []
  );
  const { width, height } = layout.dimensions;
  const { animatedHandleHeight, animatedSnapPoints, animatedContentHeight, handleContentLayout } = useBottomSheetDynamicSnapPoints([
    'CONTENT_HEIGHT',
    'CONTENT_HEIGHT'
  ]);

  const { animatedStyle, handleBack } = useNativeModalView({});

  const { runPayoutSubmit } = usePayout();
  const postPayoutSubmit = (
    destination: CreatePayoutBodyDestination,
    quoteId: string,
    purpose: CreatePayoutBodyPurpose,
    sourceOfFunds: CreatePayoutBodySourceOfFunds,
    accountId: string,
    remarks: string,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    runPayoutSubmit(
      {
        method: 'UNIONPAY',
        destination: destination,
        quoteId: quoteId,
        purpose: purpose,
        sourceOfFunds: sourceOfFunds,
        accountId: accountId,
        remarks: remarks
      },
      onSuccess,
      onError
    );
  };

  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState('');
  const submitPress = () => {
    if (unionType == UnionType.PAYMENT) {
      navigationAny.navigate(navigationNavigate, {
        screen: navigationScreen,
        params: { screen: UnionPayStackRoutes.TWOFA, onBack, wallet, calcFee, beneficiaryInformation, unionType, refresh }
      });
    } else if (unionType == UnionType.ACCOUNT) {
      setIsLoadingSubmit(true);
      postPayoutSubmit(
        {
          accountNumber: beneficiaryInformation?.account?.accountNumber,
          firstName: beneficiaryInformation?.account?.firstNameEnglish,
          firstNameCN: beneficiaryInformation?.account?.firstNameChinese,
          surname: beneficiaryInformation?.account?.lastNameEnglish,
          surnameCN: beneficiaryInformation?.account?.lastNameChinese,
          address: beneficiaryInformation?.account?.accountHolderAddress
        },
        calcFee?.id,
        beneficiaryInformation?.purpose,
        beneficiaryInformation?.fundSource,
        wallet?.id,
        beneficiaryInformation?.remark,
        (data: any) => {
          setIsLoadingSubmit(false);
          let refId = data?.data?.refId;
          navigationAny.navigate(navigationNavigate, {
            screen: navigationScreen,
            params: { screen: UnionPayStackRoutes.Placed, onBack, wallet, calcFee, beneficiaryInformation, refId, unionType, refresh }
          });
        },
        (error: any) => {
          setIsLoadingSubmit(false);
          const data: any = error.response?.data;
          const errorMessage = data && isString(data.message) ? errorParser(data.message, t) : error.message;
          setAlertMessage(errorMessage);
        }
      );
    }
  };

  const viewHight = height;

  return (
    <NativeModalView animatedStyle={animatedStyle} onBack={handleBack} showHeader={false}>
      <UtxAppEmbed>
        <View sx={{ width, height, backgroundColor: 'transparent' }}>
          <BottomSheet
            enablePanDownToClose
            index={1}
            snapPoints={animatedSnapPoints}
            handleHeight={animatedHandleHeight}
            contentHeight={animatedContentHeight}
            backdropComponent={renderBackdrop}
            handleComponent={() => <View />}>
            <View sx={sxStyles.modal}>
              <View sx={{ ...sxStyles.container, minHeight: viewHight, paddingBottom: '$12' }} onLayout={handleContentLayout}>
                <ScrollView contentContainerStyle={{ paddingTop: '$5' }} showsVerticalScrollIndicator={false}>
                  <View
                    sx={
                      layout.dimensions.height >= 689
                        ? { height: layout.dimensions.height - 120 + (alertMessage ? 40 : 0) }
                        : layout.dimensions.height <= 617
                        ? { height: 515 }
                        : { height: 575 }
                    }>
                    <View sx={{ ...sxStyles.header }}>
                      <View sx={sxStyles.topHeader}>
                        <BottomSheetHeader
                          title={t('unionPay::payment-instruction.title--payment-instruction-confirmation')}
                          onPress={() => {
                            onBack && onBack(true);
                          }}
                        />
                      </View>
                    </View>
                    <View sx={sxStyles.body}>
                      <TextBody semiBold>{t('unionPay::payment-instruction.text--confirm-payment-instruction')}</TextBody>
                      <View sx={{ gap: 8, my: '$5' }}>
                        <View sx={sxStyles.sendBox}>
                          <TextCaption semiBold>{t('unionPay::payment-instruction.text--you-send')}</TextCaption>
                          <View sx={sxStyles.amount}>
                            <CurrenciesIcon asset={calcFee?.feeCurrency} hideText={true} />
                            <TextBody semiBold>
                              {formatAmountByDecimal({
                                value: (unionType == UnionType.PAYMENT ? calcFee?.attributes?.amount : calcFee?.sourceAmount) || 0,
                                suffix: calcFee?.feeCurrency,
                                decimals: 2
                              })}
                            </TextBody>
                          </View>
                        </View>
                        <View sx={sxStyles.sendBox}>
                          <TextCaption semiBold>{t('unionPay::payment-instruction.text--recipient-receives')}</TextCaption>
                          <View sx={sxStyles.amount}>
                            <Icon name="rmb" width={28} height={28} />
                            <TextBody semiBold>
                              {formatAmountByDecimal({
                                value:
                                  (unionType == UnionType.PAYMENT
                                    ? calcFee?.attributes?.destination?.amount
                                    : calcFee?.destinationAmount) || 0,
                                suffix: 'RMB',
                                decimals: 2
                              })}
                            </TextBody>
                          </View>
                        </View>
                      </View>
                      <View sx={{ gap: 12 }}>
                        <TextBody sx={sxStyles.itemStyle}>
                          <TextBody secondary>{t('unionPay::payment-instruction.glossary.conversion-rate')}</TextBody>
                          <TextBody>
                            {formatAmountByDecimal({
                              value: 1 / calcFee?.attributes?.destination?.rate,
                              prefix: '1 RMB =',
                              suffix: calcFee?.feeCurrency,
                              decimals: 4
                            })}
                          </TextBody>
                        </TextBody>
                        <TextBody sx={{ ...sxStyles.itemStyle, ...sxStyles.topCenter }}>
                          <TextBody secondary>{t('unionPay::payment-instruction.text--from-account')}</TextBody>
                          <View sx={{ textAlign: 'right' }}>
                            <TextBody>
                              {wallet &&
                                (wallet?.accountLabel ? t(`earn-invest::glossary.${wallet?.accountLabel}`) : wallet?.accountName || ' - ')}
                            </TextBody>
                            <TextBody>{wallet && wallet?.accountNumber}</TextBody>
                          </View>
                        </TextBody>
                        <TextBody sx={{ ...sxStyles.itemStyle, ...sxStyles.topCenter }}>
                          <TextBody secondary>{t('unionPay::payment-instruction.text--to-account')}</TextBody>
                          <View sx={{ textAlign: 'right' }}>
                            <TextBody>{beneficiaryInformation?.account?.accountLabel}</TextBody>
                            <TextBody>{beneficiaryInformation?.account?.accountNumber}</TextBody>
                          </View>
                        </TextBody>
                        <TextBody sx={sxStyles.itemStyle}>
                          <TextBody secondary>{t('unionPay::payment-instruction.glossary.debit-amount')}</TextBody>
                          <TextBody sx={{ color: '$borderInputError' }}>
                            {formatAmountByDecimal({
                              value: calcFee?.instructionTotal,
                              suffix: calcFee?.feeCurrency,
                              decimals: 2
                            })}
                          </TextBody>
                        </TextBody>
                        {/* <TextBody sx={sxStyles.itemStyle}>
                        <TextBody secondary>Instruction Fee</TextBody>
                        <TextBody sx={{ color: '$borderInputError' }}>20.00 HKD</TextBody>
                      </TextBody> */}
                        <Divider sx={{ my: '$2' }} />
                        <TextBody sx={sxStyles.itemStyle}>
                          <TextBody secondary>{t('unionPay::payment-instruction.text--purpose')}</TextBody>
                          <TextBody>
                            {RemittancePurposeSelectFilters(t).find((f) => f.value === beneficiaryInformation?.purpose)?.label}
                          </TextBody>
                        </TextBody>
                        <TextBody sx={sxStyles.itemStyle}>
                          <TextBody secondary>{t('unionPay::payment-instruction.text--fund-source')}</TextBody>
                          <TextBody>
                            {FundSourceSelectFilters(t).find((f) => f.value === beneficiaryInformation?.fundSource)?.label}
                          </TextBody>
                        </TextBody>
                        <TextBody sx={{ ...sxStyles.itemStyle, ...sxStyles.topCenter }}>
                          <TextBody secondary>{t('unionPay::payment-instruction.text--remarks')}</TextBody>
                          <TextBody>{beneficiaryInformation?.remark || '-'}</TextBody>
                        </TextBody>
                      </View>
                    </View>
                  </View>
                  {!isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
                  <View sx={sxStyles.buttonBox}>
                    <UniButton
                      sx={{ height: 44 }}
                      variant={ButtonVariant.outline1}
                      label={t('global-bill-payment::glossary.back')}
                      onPress={() => {
                        onBack && onBack();
                      }}
                    />
                    <UniButton
                      sx={{ width: '100%', height: 44 }}
                      labelSx={{ color: '$balck' }}
                      label={t('unionPay::payment-instruction.button--place-instruction')}
                      onPress={submitPress}
                      loading={isLoadingSubmit}
                      disabled={unionType == UnionType.ACCOUNT && isLoadingSubmit ? true : false}
                    />
                  </View>
                </ScrollView>
              </View>
            </View>
          </BottomSheet>
        </View>
      </UtxAppEmbed>
    </NativeModalView>
  );
};

const sxStyles: Record<string, SxProp> = {
  modal: {
    width: '100%',
    padding: '$4'
  },
  container: {
    backgroundColor: '$backgroundPrimary',
    width: '100%',
    height: '100%'
  },
  header: {
    flexDirection: 'column'
    // padding: '$4'
  },
  tab: {
    backgroundColor: 'transparent',
    width: '98%'
  },
  body: {
    mt: '$1'
  },
  button: {
    width: '100%',
    height: '$11',
    mt: '$5'
  },
  viewAlert: {
    mt: '$5'
  },
  sendBox: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '$4',
    backgroundColor: '$backgroundSecondary',
    borderRadius: 10
  },
  amount: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: '$2'
  },
  itemStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 20
  },
  topCenter: {
    alignItems: 'flex-start'
  },
  buttonBox: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: '$3',
    mt: '$10'
  }
};
